import Swal from 'sweetalert2';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Chip, InputAdornment, MenuItem, OutlinedInput, Select, Tab, Tabs, ThemeProvider } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import SearchIcon from '@mui/icons-material/Search';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useLocationState } from '../../../../Common/Util/CustomHooks';
import * as request from '../../../../Common/Util/Request';
import * as excel from '../../../../Common/Util/Excel';
import * as Button from '../../../../Common/Component/Button';
import Table from '../../../../Common/Component/Table';
import { EJE_THEME } from '../../../../Common/Constant';
import './index.scss';
import dayjs from 'dayjs';

// 상태 탭과 상태 칩이 함께 쓴다. value 는 서버 status 쿼리 값이고, 전체('')의 건수는 statusCounts.total 이다
const STATUSES = [
  { value: '', label: '전체' },
  { value: '2', label: '공개', color: 'success' },
  { value: '1', label: '대기', color: 'warning' },
  { value: '0', label: '비공개', color: 'default' },
];

// placeholder 는 무엇을 입력하는지 알려 준다(Polaris IndexFilters 글쓰기 지침: 「Filter orders」 처럼 쓰고 「Enter text here」 는 쓰지 않는다)
// 한글 조합 중에 누른 Enter 는 검색으로 치지 않는다(조합이 끝날 때 keydown 이 한 번 더 온다)
const isEnter = (e) => e.key === 'Enter' && !e.nativeEvent.isComposing;

const SEARCH_OPTIONS = [
  { value: 'FK_class.title', label: '강의명', placeholder: '강의명으로 검색' },
  { value: 'FK_class_id', label: '강의ID', placeholder: '강의ID로 검색' },
  { value: 'id', label: '후기ID', placeholder: '후기ID로 검색' },
  { value: 'old_nickname', label: '회원 닉네임', placeholder: '회원 닉네임으로 검색' },
  { value: 'FK_user_id', label: '회원ID', placeholder: '회원ID로 검색' },
];

function Reviews(props) {
  const [locationState, setLocationState] = useLocationState({
    startDate: null,
    endDate: null,
    searchOption: 'FK_class.title',
    searchText: '',
    filters: [],
    status: '',
  });
  const [searchOption, setSearchOption] = useState(locationState.searchOption);
  const [searchText, setSearchText] = useState(locationState.searchText);
  const [isDownloading, setIsDownloading] = useState(null);

  // 기록 state 를 바꾸면 AdminRoute 가 페이지를 새로 마운트해 달력·입력칸·다음 클릭이 끊긴다(D-67). 그래서 날짜 입력칸의
  // onChange(달력에서 연·월을 고르는 중간 단계, 타이핑 중인 날짜)는 기억만 하고, Enter·[검색]·달력에서 날짜 확정 때
  // 검색어·다른 칸 날짜와 함께 한 번에 반영한다. 조건이 바뀌면 1페이지부터 조회한다
  const typedDates = useRef({});
  const onClickSearch = useCallback(() => {
    const typed = Object.entries(typedDates.current);
    const dates = {
      startDate: locationState.startDate ? dayjs(locationState.startDate).format('YYYY-MM-DD') : null,
      endDate: locationState.endDate ? dayjs(locationState.endDate).format('YYYY-MM-DD') : null,
      ...Object.fromEntries(typed.map(([key, { value }]) => [key, value ? value.format('YYYY-MM-DD') : null])),
    };
    // 범위는 칸마다 입력한 순간의 minDate·maxDate 가 아니라 최종 값끼리 비교한다. 두 칸을 모두 바꾸면 그때의 검사는 낡는다
    const invalid = typed.some(([, { value }]) => value && !value.isValid());
    const reversed = dates.startDate && dates.endDate && dates.startDate > dates.endDate;
    if (invalid || reversed) {
      Swal.fire({
        icon: 'warning',
        title: '기간을 확인해 주세요',
        text: '날짜가 YYYY-MM-DD 형식인지, 시작일이 종료일보다 늦지 않은지 확인해 주세요.',
      });
      return;
    }
    setLocationState({
      searchOption,
      searchText,
      filters: searchText ? [{ id: searchOption, value: searchText }] : [],
      ...dates,
      pageIndex: 0,
    });
  }, [setLocationState, searchOption, searchText, locationState.startDate, locationState.endDate]);
  const onClickReset = useCallback(() => {
    setLocationState({
      startDate: null,
      endDate: null,
      searchOption: 'FK_class.title',
      searchText: '',
      filters: [],
      pageIndex: 0,
    });
  }, [setLocationState]);

  const onClickCSV = useCallback(() => {
    setIsDownloading(true);
    request
      .reviews('', '', { page: 1, limit: 99999 }) // temp
      .then(({ reviews }) => {
        const data = reviews.rows.reduce(
          (acc, cur) => [
            ...acc,
            {
              '후기 ID': cur.id,
              '후기 작성 회원 ID': cur.FK_user?.id || '',
              '후기 작성 회원 닉네임': cur.FK_user?.nickname || '',
              '후기 상태': STATUSES.find((item) => item.value === String(cur.status))?.label || '알수없음',
              '최초 등록 강의 ID': cur.FK_class_id,
              '최초 등록 강의명': cur.FK_class?.title,
              '현재 노출 강의': cur.selectedReviews,
              총점: cur.rate,
              총평: cur.content,
              '수업 내용 만족도(점수)': cur.toStaffs?.contentRating,
              '수업 내용 만족도(주관식)': cur.toStaffs?.contentComment,
              '강사 만족도(점수)': cur.toStaffs?.tutorRating,
              '강사 만족도(주관식)': cur.toStaffs?.tutorComment,
              '수업 시간 만족도(점수)': cur.toStaffs?.timeRating,
              '수업 시간 만족도(주관식)': cur.toStaffs?.timeComment,
              '엔터잡에듀에 기대하는 것': cur.toStaffs?.suggestionComment,
            },
          ],
          [],
        );
        excel.downloadXLSX(data, `리뷰 목록 ${dayjs().format('YYYY-MM-DD')}`);
      })
      .finally(() => setIsDownloading(false));
  }, []);

  const [reviews, setReviews] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusCounts, setStatusCounts] = useState({});

  const columns = useMemo(
    () => [
      { Header: '후기ID', accessor: 'id', width: 80 },
      { Header: '후기 작성 회원', accessor: 'old_nickname', width: 140 },
      { Header: '노출 강의 ID', accessor: 'FK_class_id', width: 100 },
      {
        Header: '노출 강의명',
        accessor: 'FK_class.title',
        Cell: ({ value }) => (
          <div className="reviews__title" title={value}>
            {value}
          </div>
        ),
      },
      { Header: '평점', accessor: 'rate', width: 70, Cell: ({ value }) => `★ ${value}` },
      {
        Header: '상태',
        accessor: 'status',
        width: 90,
        Cell: ({ value }) => {
          const status = STATUSES.find((item) => item.value === String(value));
          return status ? (
            <Chip size="small" label={status.label} color={status.color} />
          ) : (
            <Chip size="small" variant="outlined" label="알수없음" />
          );
        },
      },
      {
        Header: '후기 보기',
        width: 90,
        accessor: (row) => (
          <Button.MUI
            size="small"
            variant="outlined"
            color="pink"
            endIcon={<OpenInNewIcon fontSize="inherit" />}
            sx={{
              py: 0,
              '&:hover': { color: 'pink.contrastText', backgroundColor: 'pink.main', borderColor: 'pink.main' },
            }}
            onClick={() => window.open(`./lecture/new/${row.id}`)}
          >
            보기
          </Button.MUI>
        ),
      },
    ],
    [],
  );

  const fetchData = useCallback(
    ({ pageIndex, pageSize }) => {
      const params = {
        page: pageIndex + 1,
        limit: pageSize,
        filtered: JSON.stringify(locationState.filters),
      };

      if (locationState.startDate) {
        params.start_date = dayjs(locationState.startDate).format('YYYY-MM-DD');
      }
      if (locationState.endDate) {
        params.end_date = dayjs(locationState.endDate).format('YYYY-MM-DD');
      }
      if (locationState.status) {
        params.status = locationState.status;
      }

      request.reviews('', '', params).then(({ reviews, statusCounts }) => {
        setReviews(reviews.rows);
        setTotal(reviews.count);
        setStatusCounts(statusCounts || {});
      });
    },
    [locationState.filters, locationState.startDate, locationState.endDate, locationState.status],
  );

  const [selectedReviews, setSelectedReviews] = useState([]);
  const [tableKey, setTableKey] = useState(0);
  const deleteSelectedReviews = useCallback(() => {
    request.deleteReviews({ selectedReviews }).then(() => {
      Swal.fire({
        title: '삭제 완료',
        text: '선택한 후기가 삭제되었습니다.',
        icon: 'success',
      }).then(() => {
        setTableKey((prev) => prev + 1);
      });
    });
  }, [selectedReviews]);
  const onChangeSelectedRows = useCallback((rows) => {
    setSelectedReviews(rows.map((row) => row.original.id));
  }, []);
  // 삭제는 행을 고른 뒤에만 보이는 일괄 작업이고, 실행 전에 확인받는다(Polaris IndexTable bulk actions, Ant Design Pro ProTable alert)
  const onClickDelete = useCallback(() => {
    Swal.fire({
      icon: 'warning',
      title: `후기 ${selectedReviews.length}개를 삭제할까요?`,
      text: '삭제한 후기는 이 목록에 더는 보이지 않습니다.',
      showCancelButton: true,
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
      confirmButtonColor: '#d32f2f',
    }).then(({ isConfirmed }) => {
      if (isConfirmed) deleteSelectedReviews();
    });
  }, [selectedReviews, deleteSelectedReviews]);
  const datePickerProps = (key) => ({
    views: ['year', 'month', 'day'],
    format: 'YYYY-MM-DD',
    onChange: (value) => {
      typedDates.current[key] = { value };
    },
    onAccept: (value) => {
      typedDates.current[key] = { value };
      onClickSearch();
    },
    slotProps: {
      actionBar: { actions: ['clear'] },
      textField: { size: 'small', onKeyDown: (e) => isEnter(e) && onClickSearch() },
    },
  });

  const searchPlaceholder = SEARCH_OPTIONS.find(({ value }) => value === searchOption)?.placeholder;

  return (
    <ThemeProvider theme={EJE_THEME}>
      <div className="reviews">
        <div className="reviews__card">
          <div className="reviews__toolbar">
            {/* AdminRoute 가 기록이 바뀔 때마다 이 페이지를 새로 마운트하고 Table 은 기록 state 의 pageIndex 로
                시작하므로, pageIndex 0 만 넣으면 1페이지부터 다시 조회한다 */}
            <Tabs
              className="reviews__tabs"
              value={locationState.status || ''}
              onChange={(e, value) => setLocationState({ status: value, pageIndex: 0 })}
            >
              {STATUSES.map(({ value, label }) => {
                const count = statusCounts[value || 'total'];
                return (
                  <Tab
                    key={label}
                    value={value}
                    label={count === undefined ? label : `${label} ${count.toLocaleString()}`}
                  />
                );
              })}
            </Tabs>
            <div className="reviews__actions">
              <Button.MUI
                variant="outlined"
                color="pink"
                startIcon={<FileDownloadOutlinedIcon />}
                disabled={!!isDownloading}
                onClick={onClickCSV}
              >
                {isDownloading ? '다운로드 중' : '전체 엑셀 다운로드'}
              </Button.MUI>
              <Button.MUI
                variant="contained"
                color="pink"
                disableElevation
                startIcon={<AddIcon />}
                onClick={() => window.open(`/review/lecture/new`)}
              >
                후기 등록
              </Button.MUI>
            </div>
          </div>
          <div className="reviews__filters">
            <Select size="small" color="pink" value={searchOption} onChange={(e) => setSearchOption(e.target.value)}>
              {SEARCH_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value} sx={{ fontSize: '0.875rem' }}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <OutlinedInput
              className="reviews__search"
              size="small"
              color="pink"
              value={searchText}
              placeholder={searchPlaceholder}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (isEnter(e)) onClickSearch();
              }}
            />
            <span className="reviews__filter-label">작성일</span>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                {...datePickerProps('startDate')}
                value={locationState.startDate ? dayjs(locationState.startDate) : null}
                maxDate={locationState.endDate ? dayjs(locationState.endDate) : undefined}
              />
              ~
              <DatePicker
                {...datePickerProps('endDate')}
                value={locationState.endDate ? dayjs(locationState.endDate) : null}
                minDate={locationState.startDate ? dayjs(locationState.startDate) : undefined}
              />
            </LocalizationProvider>
            <Button.MUI variant="contained" color="pink" disableElevation onClick={onClickSearch}>
              검색
            </Button.MUI>
            <Button.MUI color="inherit" onClick={onClickReset}>
              초기화
            </Button.MUI>
          </div>
          <div className={`reviews__summary${selectedReviews.length ? ' reviews__summary--selected' : ''}`}>
            {selectedReviews.length ? (
              <>
                <strong>{selectedReviews.length}개 선택됨</strong>
                <Button.MUI size="small" color="inherit" onClick={() => setTableKey((prev) => prev + 1)}>
                  선택 해제
                </Button.MUI>
                <Button.MUI
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={onClickDelete}
                >
                  선택한 후기 삭제
                </Button.MUI>
              </>
            ) : (
              <span>총 {total.toLocaleString()}건</span>
            )}
          </div>
          <Table
            key={tableKey}
            variant="clean"
            data={reviews}
            total={total}
            columns={columns}
            fetchData={fetchData}
            pageSize={20}
            onChangeSelectedRows={onChangeSelectedRows}
          />
        </div>
      </div>
    </ThemeProvider>
  );
}

export default Reviews;
